import type {
  BulkUploadIssueRowDto,
  BulkUploadResultDataDto,
} from "../../data/dto/BulkUploadDto";
import type { BulkUploadResultEntity } from "../../domain/entities/BulkUploadEntity";

const OTHER_BUCKET_FIELDS = new Set([
  "zoho_creation",
  "gstin",
  "state",
  "pan_tax_id",
  "pan_tax_registration_no",
  "insert",
  "currency",
  "embedding",
  "tds_tax_rate",
]);

function dtoIssueHeuristicKind(
  e: BulkUploadIssueRowDto,
): "skipped" | "validation" | "other" {
  const msg = e.message ?? "";
  const field = e.field ?? "";
  if (/\balready exists\b/i.test(msg)) {
    return "skipped";
  }
  if (field.toLowerCase() === "duplicate") {
    return "validation";
  }
  if (OTHER_BUCKET_FIELDS.has(field)) {
    return "other";
  }
  return "validation";
}

function partitionMergedDtoErrors(errors: BulkUploadIssueRowDto[]): {
  validationFailures: BulkUploadIssueRowDto[];
  skippedExisting: BulkUploadIssueRowDto[];
  otherFailures: BulkUploadIssueRowDto[];
} {
  if (errors.length === 0) {
    return {
      validationFailures: [],
      skippedExisting: [],
      otherFailures: [],
    };
  }

  const hasStatus = errors.some((row) => Boolean(row.status));

  const validationFailures: BulkUploadIssueRowDto[] = [];
  const skippedExisting: BulkUploadIssueRowDto[] = [];
  const otherFailures: BulkUploadIssueRowDto[] = [];

  const pushByHeuristic = (row: BulkUploadIssueRowDto) => {
    switch (dtoIssueHeuristicKind(row)) {
      case "skipped":
        skippedExisting.push(row);
        break;
      case "other":
        otherFailures.push(row);
        break;
      default:
        validationFailures.push(row);
    }
  };

  if (hasStatus) {
    for (const row of errors) {
      if (row.status === "already_exists") {
        skippedExisting.push(row);
      } else if (
        row.status === "zoho_failed" ||
        row.status === "db_failed"
      ) {
        otherFailures.push(row);
      } else if (row.status === "validation_failed") {
        validationFailures.push(row);
      } else {
        pushByHeuristic(row);
      }
    }
  } else {
    for (const row of errors) {
      pushByHeuristic(row);
    }
  }

  return { validationFailures, skippedExisting, otherFailures };
}

/**
 * Resolved bucket lists for admin upload UI — expands legacy `errors` when dedicated arrays are empty.
 */
export function expandFlatBulkUploadDtoBuckets(d: BulkUploadResultDataDto): {
  validationFailures: BulkUploadIssueRowDto[];
  skippedExisting: BulkUploadIssueRowDto[];
  otherFailures: BulkUploadIssueRowDto[];
  /** For summary stat: uses API counts when present, otherwise row totals. */
  otherErrorStatCount: number;
} {
  const zArr = d.zoho_failures ?? [];
  const bArr = d.db_failures ?? [];

  let validationFailures = d.validation_failures ?? [];
  let skippedExisting = d.skipped_existing ?? [];
  let otherFailures: BulkUploadIssueRowDto[] = [...zArr, ...bArr];

  const needsPartition =
    validationFailures.length === 0 &&
    skippedExisting.length === 0 &&
    zArr.length === 0 &&
    bArr.length === 0 &&
    (d.errors?.length ?? 0) > 0;

  if (needsPartition) {
    const part = partitionMergedDtoErrors(d.errors);
    validationFailures = part.validationFailures;
    skippedExisting = part.skippedExisting;
    otherFailures = part.otherFailures;
  }

  const otherErrorStatCount = needsPartition
    ? otherFailures.length
    : (d.zoho_failed_count ?? zArr.length) +
      (d.db_failed_count ?? bArr.length);

  return {
    validationFailures,
    skippedExisting,
    otherFailures,
    otherErrorStatCount,
  };
}

export function bulkUploadEntityHasProblems(
  r: BulkUploadResultEntity,
): boolean {
  return (
    r.validationFailedCount > 0 ||
    r.alreadyExistsCount > 0 ||
    r.otherFailedCount > 0 ||
    r.validationFailures.length > 0 ||
    r.skippedExisting.length > 0 ||
    r.otherFailures.length > 0 ||
    r.errors.length > 0 ||
    r.insertFailedCount > 0
  );
}

export function bulkUploadDtoHasProblems(d: BulkUploadResultDataDto): boolean {
  return (
    (d.validation_failed_count ?? d.validated_failed) > 0 ||
    (d.already_exists_count ?? 0) > 0 ||
    (d.zoho_failed_count ?? 0) > 0 ||
    (d.db_failed_count ?? 0) > 0 ||
    (d.validation_failures?.length ?? 0) > 0 ||
    (d.skipped_existing?.length ?? 0) > 0 ||
    (d.zoho_failures?.length ?? 0) > 0 ||
    (d.db_failures?.length ?? 0) > 0 ||
    (d.errors?.length ?? 0) > 0 ||
    d.insert_failed_count > 0
  );
}

export function bulkUploadIssuePrimaryFromDto(row: {
  row: number;
  display_name?: string;
}): string {
  const nm = row.display_name?.trim();
  return nm ? `Row ${row.row} — ${nm}` : `Row ${row.row}`;
}
